import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../config/api';
import logo from '../../assets/waseeny-logo.png';
import useCart from '../../Hook/useCart';
import './SearchPage.css';
import useSearch from '../../Hook/useSearch';
import { useTranslate } from '../../Hook/useTranslate';
import useDynamicTranslation from '../../Hook/useDynamicTranslation';
import { getCurrencyFromPhone } from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';
import { getDeliveryInfo } from '../../utils/deliveryUtils';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import defaultRestoImage from '../../assets/default-resto-image.png';
import defaultItemImage from '../../assets/default-item-image.png';
import { SearchSkeleton } from '../Common/PageSkeleton';

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslate();
  const { cart, addItem } = useCart();

  // User location for distance calculation
  const userLocation = useMemo(() => {
    try {
      const stored = localStorage.getItem('userFullAddress');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          lat: parsed.coordinates?.[1] || 51.4994,
          lng: parsed.coordinates?.[0] || 7.0833
        };
      }
    } catch (e) { }
    return { lat: 51.4994, lng: 7.0833 }; // Default fallback
  }, []);

  const BackIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M14 5l-6 6 6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const rawQuery = new URLSearchParams(location.search).get('q');
  const query = rawQuery ? decodeURIComponent(rawQuery) : '';

  const [results, setResults] = useState({ restaurants: [], products: [] });
  const [searchInput, setSearchInput] = useState(query);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { suggestions } = useSearch(searchInput);

  // Filter States
  const currencySymbol = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const phone = user.mobile || user.phone || "";
      return getCurrencyFromPhone(phone);
    } catch (e) {
      return '';
    }
  }, []);

  const [activeFilters, setActiveFilters] = useState({
    vegOnly: false,
    topRated: false,
    priceRange: 'all',
  });

  const extractText = (data) => {
    if (!data) return "";
    if (typeof data === "object") {
      return data.en || data.En || Object.values(data)[0] || "";
    }
    return String(data);
  };

  const allTexts = useMemo(() => [
    ...results.restaurants.map(r => extractText(r.name)),
    ...results.products.map(p => extractText(p.name)),
    ...results.products.map(p => extractText(p.description))
  ].filter(Boolean), [results]);

  const { getText } = useDynamicTranslation(allTexts);

  const fetchResults = async (searchQuery) => {
    if (!searchQuery) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch suggestions API (Public) to get dishes from ALL restaurants matching the query
      const suggRes = await api.get(`/search/suggestions?q=${searchQuery}`);
      const suggDishes = (Array.isArray(suggRes.data) ? suggRes.data : (suggRes.data?.data || [])).filter(item => item.type === 'dish').map(item => ({
        _id: item.id || item._id,
        name: { en: item.text || item.name },
        image: item.image,
        basePrice: item.basePrice || 250,
        description: { en: item.description || "Delicious freshly prepared item" },
        restaurant: item.restaurantId,
        restaurantName: item.restaurantName
      }));

      // 2. Fetch nearby restaurants for the user's location 
      let nearbyRestList = [];
      try {
        const landingRes = await api.get(`/search/landing?lat=${userLocation.lat}&lng=${userLocation.lng}`);
        nearbyRestList = landingRes.data?.nearbyRestaurants || [];
      } catch (err) {
        // Fallback for unauthenticated users
        const fallbackRes = await api.get(`/restaurant?lat=${userLocation.lat}&lng=${userLocation.lng}&radiusKm=100`);
        nearbyRestList = fallbackRes.data?.restaurants || fallbackRes.data || [];
      }
      const nearbyRestIds = new Set(nearbyRestList.map(r => r._id?.toString() || r.id?.toString()));

      // 3. Fetch search results without lat/lng
      let rawRestaurants = [];
      let rawProductsFromSearch = [];
      try {
        const res = await api.get(`/search?q=${searchQuery}`);
        const data = res.data?.results || {};
        rawRestaurants = Array.isArray(data.restaurants) ? data.restaurants : [];
        rawProductsFromSearch = Array.isArray(data.products) ? data.products : [];
      } catch (err) {
        // Fallback for unauthenticated users: filter nearbyRestList by name/cuisine matching searchQuery
        const qLower = searchQuery.toLowerCase();
        rawRestaurants = nearbyRestList.filter(r => {
          const rName = (r.name?.en || r.name || "").toLowerCase();
          const rCuisines = Array.isArray(r.cuisine) ? r.cuisine : (typeof r.cuisine === 'string' ? [r.cuisine] : []);
          return rName.includes(qLower) || rCuisines.some(c => extractText(c).toLowerCase().includes(qLower));
        });
      }

      // Filter restaurants: must be in nearbyRestIds
      let nearbyRestaurants = rawRestaurants.filter(r => {
        const rId = r._id?.toString() || r.id?.toString();
        return rId && nearbyRestIds.has(rId);
      });

      const restaurantsWithDistance = nearbyRestaurants.map((rest, index) => {
        const restWithCoords = {
          ...rest,
          lat: rest.lat || rest.latitude || rest.location?.coordinates?.[1] || (userLocation.lat + (index % 2 === 0 ? 0.008 : 0.015)),
          lng: rest.lng || rest.longitude || rest.location?.coordinates?.[0] || (userLocation.lng + (index % 2 === 0 ? 0.008 : -0.012))
        };
        const info = getDeliveryInfo(restWithCoords, userLocation.lat, userLocation.lng);
        return { ...restWithCoords, deliveryInfo: info };
      });

      // Sort by distance
      let sortedRestaurants = [...restaurantsWithDistance].sort((a, b) => {
        return (a.deliveryInfo?.distanceKm || 999) - (b.deliveryInfo?.distanceKm || 999);
      });

      // Merge products
      let allRawProducts = [...rawProductsFromSearch, ...suggDishes];
      const uniqueProductsMap = new Map();
      allRawProducts.forEach(product => {
        if (product._id) uniqueProductsMap.set(product._id.toString(), product);
      });
      allRawProducts = Array.from(uniqueProductsMap.values());

      let nearbyProducts = allRawProducts.filter(product => {
        let restId = "";
        if (product.restaurant && typeof product.restaurant === 'object') {
          restId = product.restaurant._id || product.restaurant.id;
        } else {
          restId = product.restaurant || product.restaurantId;
        }
        return restId && nearbyRestIds.has(restId.toString());
      });

      setResults({
        restaurants: sortedRestaurants,
        products: nearbyProducts
      });
    } catch (err) {
      console.error("Search failed:", err);
      setError("Failed to fetch search results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults(query);
    setSearchInput(query);
  }, [query]);

  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentSearches') || '[]');
    } catch (e) { return []; }
  });

  const saveRecentSearch = (term) => {
    if (!term || term.trim().length < 2) return;
    const newRecent = [term.trim(), ...recentSearches.filter(s => s !== term.trim())].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('recentSearches', JSON.stringify(newRecent));
  };

  const handleSearchSubmit = (q) => {
    const finalQuery = q || searchInput;
    if (finalQuery.trim()) {
      saveRecentSearch(finalQuery);
      navigate(`/search?q=${encodeURIComponent(finalQuery.trim())}`);
    }
  };

  const handleSelectSuggestion = (item) => {
    const term = item.text || item.name || (typeof item === 'string' ? item : "");
    saveRecentSearch(term);

    if (item.type === 'restaurant' && item.id) {
      navigate(`/restaurant/${item.id}`);
    } else if (item.type === 'dish' && item.restaurantId) {
      navigate(`/restaurant/${item.restaurantId}`);
    } else {
      setSearchInput(term);
      handleSearchSubmit(term);
    }
  };

  // Apply Advanced Filters to Products and Restaurants
  const filteredData = useMemo(() => {
    let filteredProducts = [...results.products];
    let filteredRestaurants = [...results.restaurants];

    if (activeFilters.vegOnly) {
      filteredProducts = filteredProducts.filter(p => p.isVeg);
    }

    if (activeFilters.topRated) {
      filteredRestaurants = filteredRestaurants.filter(r => (r.rating?.average || r.rating) >= 4);
    }

    if (activeFilters.priceRange !== 'all') {
      const [min, max] = activeFilters.priceRange.split('-').map(Number);
      filteredRestaurants = filteredRestaurants.filter(r => {
        const price = r.averageOrderValue || r.priceRange?.average || r.minOrderValue || 0;
        return price >= min && (max ? price <= max : true);
      });
    }

    return { products: filteredProducts, restaurants: filteredRestaurants };
  }, [results, activeFilters]);

  const handleAddToCart = (item) => {
    addItem(item);
  };

  const groupedProducts = (filteredData.products || []).reduce((acc, product) => {
    let restId = "";
    let restaurantData = null;

    if (product.restaurant && typeof product.restaurant === 'object') {
      restId = product.restaurant._id || product.restaurant.id;
      restaurantData = product.restaurant;
    } else {
      restId = product.restaurant || product.restaurantId;
      restaurantData = { _id: restId, name: product.restaurantName };
    }

    if (!restId) return acc;

    let fullRestaurant = results.restaurants.find(r => r._id === restId) || restaurantData;
    fullRestaurant = {
      ...fullRestaurant,
      lat: fullRestaurant.lat || fullRestaurant.latitude || fullRestaurant.location?.coordinates?.[1] || (userLocation.lat + (Math.random() * 0.02)),
      lng: fullRestaurant.lng || fullRestaurant.longitude || fullRestaurant.location?.coordinates?.[0] || (userLocation.lng + (Math.random() * 0.02))
    };

    const restName = getText(fullRestaurant?.name) || product.restaurantName || "Other";
    const groupKey = `${restId}-${restName}`;

    if (!acc[groupKey]) {
      const restDeliveryInfo = getDeliveryInfo(fullRestaurant, userLocation.lat, userLocation.lng);
      acc[groupKey] = { info: fullRestaurant, items: [], restId, deliveryInfo: restDeliveryInfo };
    }
    acc[groupKey].items.push(product);
    return acc;
  }, {});

  const sortedGroupedProducts = Object.entries(groupedProducts)
    .sort((a, b) => {
      const distA = a[1].deliveryInfo?.distanceKm || 999;
      const distB = b[1].deliveryInfo?.distanceKm || 999;
      return distA - distB;
    });

  if (loading) {
    return (
      <div className="search-page-container">
        <header className="search-page-header">
          <button onClick={() => navigate(-1)} className="search-back-btn">
            <BackIcon />
          </button>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-page-input"
              value={searchInput}
              placeholder={t("searchPlaceholder") || "Search for food, restaurants, dishes..."}
              readOnly
            />
          </div>
        </header>
        <SearchSkeleton />
      </div>
    );
  }

  if (error) return (
    <div className="search-page-container">
      <header className="search-nav" style={{ display: 'flex', alignItems: 'center', padding: '15px', borderBottom: '1px solid #eee' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px', display: 'flex', alignItems: 'center' }}>
          <BackIcon />
        </button>
        <img src={logo} alt="Waseeny" style={{ width: '100px', cursor: 'pointer' }} onClick={() => navigate('/home')} />
      </header>
      <main style={{ padding: '20px' }}>
        <div style={{ color: '#d32f2f', textAlign: 'center', padding: '20px' }}>
          {error}
        </div>
      </main>
    </div>
  );

  return (
    <div className="search-page-container">
      <header className="search-page-header">
        <button onClick={() => navigate(-1)} className="search-back-btn">
          <BackIcon />
        </button>
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-page-input"
            value={searchInput}
            placeholder={t("searchPlaceholder")}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 250)}
          />
          {isSearchFocused && ((searchInput && suggestions?.length > 0) || (!searchInput && recentSearches.length > 0)) && (
            <div className="search-page-suggestions">
              {!searchInput && recentSearches.length > 0 && (
                <div className="recent-searches-header">
                  <span className="recent-title">{t("recentSearches") || "Recent Searches"}</span>
                  <button
                    className="clear-recent-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecentSearches([]);
                      localStorage.removeItem('recentSearches');
                    }}
                  >
                    {t("clear") || "Clear"}
                  </button>
                </div>
              )}

              {!searchInput ? (
                recentSearches.map((term, idx) => (
                  <div key={idx} className="search-suggestion-item recent" onClick={() => handleSelectSuggestion(term)}>
                    <span className="suggestion-icon">🕒</span>
                    <span className="suggestion-text">{term}</span>
                  </div>
                ))
              ) : (
                suggestions.map((s, i) => (
                  <div key={i} className="search-suggestion-item" onClick={() => handleSelectSuggestion(s)}>
                    <span className="suggestion-icon">
                      {s.image ? (
                        <img
                          src={getAbsoluteImageUrl(s.image || s.bannerImage || s.restaurantImages?.[0]) || defaultRestoImage}
                          alt=""
                          className="suggestion-img"
                          onError={(e) => { e.target.src = s.type === 'restaurant' ? defaultRestoImage : defaultItemImage; }}
                        />
                      ) : (
                        s.type === 'restaurant' ? '🏪' : '🍕'
                      )}
                    </span>
                    <span className="suggestion-text">{s.text}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <img src={logo} alt="Waseeny" className="search-logo" onClick={() => navigate('/home')} />
      </header>

      <div className="advanced-filters-bar">
        <button
          className={`filter-chip ${activeFilters.vegOnly ? 'active' : ''}`}
          onClick={() => setActiveFilters(prev => ({ ...prev, vegOnly: !prev.vegOnly }))}
        >
          🟢 {t("pureVeg") || "Pure Veg"}
        </button>
        <button
          className={`filter-chip ${activeFilters.topRated ? 'active' : ''}`}
          onClick={() => setActiveFilters(prev => ({ ...prev, topRated: !prev.topRated }))}
        >
          ⭐ 4.0+ {t("rating") || "Rating"}
        </button>
        <select
          className="filter-select"
          value={activeFilters.priceRange}
          onChange={(e) => setActiveFilters(prev => ({ ...prev, priceRange: e.target.value }))}
        >
          <option value="all">💰 {t("anyPrice") || "Any Price"}</option>
          <option value="0-200">0 {currencySymbol} - 200 {currencySymbol}</option>
          <option value="200-500">200 {currencySymbol} - 500 {currencySymbol}</option>
          <option value="500-1000">500 {currencySymbol} - 1000 {currencySymbol}</option>
          <option value="1000">1000+ {currencySymbol}</option>
        </select>
      </div>

      <main style={{ padding: '20px', paddingBottom: (cart.items?.length || 0) > 0 ? '100px' : '20px' }}>
        {loading ? (
          <SearchSkeleton />
        ) : Object.keys(groupedProducts).length === 0 && (results.restaurants || []).length === 0 ? (
          <div className="no-results-card" style={{
            textAlign: 'center',
            padding: '60px 20px',
            maxWidth: '560px',
            margin: '40px auto',
            background: '#ffffff',
            borderRadius: '24px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              background: 'linear-gradient(135deg, #fff1f1 0%, #ffe4e4 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px'
            }}>
              🔍
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', margin: '0 0 10px 0' }}>
              {t("noMatchesFound") || "No matches found for"} "{query || searchInput}"
            </h3>
            <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              Try searching for popular cuisines or explore trending dishes around you.
            </p>

            {/* Popular quick-search suggestions */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                Popular Searches
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                {['Pizza', 'Burger', 'Sushi', 'Shawarma', 'Biryani', 'Pasta', 'Dessert'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      setSearchInput(tag);
                      navigate(`/search?q=${encodeURIComponent(tag)}`);
                    }}
                    style={{
                      background: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#495057',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ff1e1e';
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.borderColor = '#ff1e1e';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.color = '#495057';
                      e.currentTarget.style.borderColor = '#e9ecef';
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => navigate('/home')}
              style={{
                background: 'linear-gradient(135deg, #ff1e1e 0%, #e01b1b 100%)',
                color: '#fff',
                border: 'none',
                padding: '12px 28px',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 30, 30, 0.25)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t("backToHome") || "Explore All Restaurants"}
            </button>
          </div>
        ) : (
          <>
            {/* Show Restaurants */}
            {(results.restaurants || []).length > 0 && (
              <section style={{ marginBottom: '30px' }}>
                <h2 style={{ borderBottom: '3px solid #ff1e1e', paddingBottom: '10px', marginBottom: '15px' }}>
                  {t("restaurants")}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                  {(results.restaurants || []).map(restaurant => {
                    const restName = getText(restaurant?.name);
                    let restRating = 'N/A';
                    if (restaurant?.rating) {
                      if (typeof restaurant.rating === 'object') {
                        restRating = restaurant.rating.average || 'N/A';
                      } else {
                        restRating = restaurant.rating;
                      }
                    }
                    return (
                      <div
                        key={restaurant._id}
                        onClick={() => navigate(`/restaurant/${restaurant._id}`)}
                        style={{ border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s', background: '#fff' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div style={{ height: '140px', background: '#f8f8f8', position: 'relative' }}>
                          <img
                            src={getAbsoluteImageUrl(restaurant.image || restaurant.bannerImage || restaurant.restaurantImages?.[0]) || defaultRestoImage}
                            alt={restName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.src = defaultRestoImage; }}
                          />
                          {restaurant.deliveryInfo?.deliveryFee === 'Free Delivery' && (
                            <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#48c479', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                              Free Delivery
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '12px' }}>
                          <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: 'bold', color: '#111' }}>{restName}</h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <span style={{ color: '#f39c12' }}>★</span>
                              <span style={{ fontWeight: 'bold', color: '#111' }}>{restRating}</span>
                            </div>
                            <span>•</span>
                            <span style={{ color: '#E31E24', fontWeight: '600' }}>{restaurant.deliveryInfo?.distLabel || "Distance N/A"}</span>
                            <span>•</span>
                            <span>{restaurant.deliveryInfo?.timeLabel || '25 min'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Show Products Grouped by Restaurant */}
            {sortedGroupedProducts.length > 0 && (
              <section style={{ marginBottom: '30px' }}>
                <h2 style={{ borderBottom: '3px solid #ff1e1e', paddingBottom: '10px', marginBottom: '15px' }}>
                  {t("products")}
                </h2>
                {sortedGroupedProducts.map(([groupKey, data]) => {
                  const displayName = getText(data.info?.name) || groupKey.split('-').slice(1).join('-') || "Restaurant";
                  return (
                    <section key={groupKey} style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                        <h3
                          onClick={() => navigate(`/restaurant/${data.restId}`)}
                          style={{
                            borderBottom: '2px solid #ff1e1e',
                            display: 'inline-block',
                            cursor: 'pointer',
                            padding: '5px 0',
                            margin: 0
                          }}
                        >
                          {displayName} ▶
                        </h3>
                        {data.deliveryInfo?.distLabel && (
                          <span style={{ color: '#E31E24', fontWeight: 'bold', fontSize: '14px' }}>
                            • {data.deliveryInfo.distLabel}
                          </span>
                        )}
                        {data.deliveryInfo?.timeLabel && (
                          <span style={{ color: '#666', fontSize: '14px' }}>
                            • {data.deliveryInfo.timeLabel}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                        {data.items.map(product => {
                          const prodName = getText(product?.name);
                          const prodDescription = getText(product?.description).substring(0, 60);
                          const productCurrencySymbol = getCurrencyFromPhone(product.restaurant?.contactNumber || data.info?.contactNumber);
                          return (
                            <div
                              key={product._id}
                              onClick={() => navigate(`/restaurant/${data.restId}`)}
                              style={{
                                border: '1px solid #eee', borderRadius: '12px', padding: '15px', display: 'flex',
                                justifyContent: 'space-between', flexDirection: 'column', cursor: 'pointer',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease', background: '#fff'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 5px 0', fontSize: '15px', fontWeight: 'bold', color: '#111' }}>{prodName || 'Product'}</h4>
                                <p style={{ color: '#ff1e1e', fontWeight: 'bold', margin: '5px 0', fontSize: '15px' }}>{formatMoney(product.basePrice, { symbol: productCurrencySymbol })}{product.unit ? ` / ${product.unit}` : ''}</p>
                                <p style={{ fontSize: '12px', color: '#666', margin: '0', lineHeight: '1.4' }}>{prodDescription}...</p>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                                <img
                                  src={product.image ? getAbsoluteImageUrl(product.image) : defaultItemImage}
                                  alt={prodName || 'Product'}
                                  style={{ width: '80px', height: '80px', borderRadius: '10px', objectFit: 'cover' }}
                                  onError={(e) => { e.target.src = defaultItemImage; }}
                                />
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                                  style={{ background: '#fff', border: '2px solid #ff1e1e', color: '#ff1e1e', padding: '6px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s ease' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#ff1e1e'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#ff1e1e'; }}
                                >
                                  {t("add")} +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </section>
            )}
          </>
        )}
      </main>

      {/* card footer */}
      {(cart.items?.length || 0) > 0 && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#ff1e1e', color: '#fff', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1000, boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
          <div style={{ fontWeight: 'bold' }}>{cart.items.length} {t("itemsAddedToCart")}</div>
          <button
            onClick={() => navigate('/checkout')}
            style={{ background: '#fff', color: '#ff1e1e', border: 'none', padding: '10px 25px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            {t("viewCart")}
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchPage;